'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { homeV2Glass, type HomeV2GlassVariant } from '@/lib/home/homeV2Theme'
import {
  SURFACE_FG_ATTR,
  surfaceForegroundStyle,
} from '@/lib/design/surfaceForeground'

export interface HomeV2GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  /** @deprecated use variant="hero" */
  emphasis?: boolean
  variant?: HomeV2GlassVariant
}

function resolveVariant(emphasis?: boolean, variant?: HomeV2GlassVariant): HomeV2GlassVariant {
  if (variant) return variant
  if (emphasis) return 'hero'
  return 'surface'
}

/**
 * V2 Home glass — warm amber frost with edge lighting and layered depth.
 * Sets dark-glass foreground so body copy inherits readable cream, not --wag-dark.
 */
export function HomeV2GlassCard({
  children,
  className,
  emphasis = false,
  variant,
  style,
  ...props
}: HomeV2GlassCardProps) {
  const resolved = resolveVariant(emphasis, variant)
  const tokens = homeV2Glass[resolved]
  const heroBloom =
    resolved === 'hero' && 'ambientBloom' in tokens
      ? (tokens as (typeof homeV2Glass)['hero']).ambientBloom
      : null

  return (
    <div
      className={cn('relative overflow-hidden border backdrop-blur-[22px]', tokens.radius, className)}
      data-surface-fg={SURFACE_FG_ATTR.darkGlass}
      style={{
        background: tokens.background,
        borderColor: tokens.border,
        boxShadow: tokens.shadow,
        WebkitBackdropFilter: `blur(${homeV2Glass.blur})`,
        backdropFilter: `blur(${homeV2Glass.blur})`,
        ...surfaceForegroundStyle.darkGlass,
        ...style,
      }}
      {...props}
    >
      {heroBloom && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-24 opacity-80"
          style={{ background: heroBloom }}
        />
      )}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent"
      />
      <div className="relative">{children}</div>
    </div>
  )
}
